import { useProBuyerProjectsAccess } from "@/Apollo/hooks/useProBuyerProjectsAccess";
import { ProBuyerProjectsAccess } from "@/Apollo/schema";
import { useAuth } from "@/auth";
import { PRO_BUYER_PROJECTS_UI_GATE } from "@/features/pro-buyer-projects/static/pro-buyer-projects.gate";
import { PRO_WORKSPACES_GATE } from "@/features/pro-buyer-workspaces/static/workspaces.gate";
import { useFeatureGate } from "@/features/statsig/gate.hooks";

/**
 * WHO-6629. Who sees the project workspaces: the Statsig gate
 * `pro_buyer_project_workspaces` AND the step 6 access (Statsig
 * `pro_buyer_projects_ui` + the gateway's FULL / READ_ONLY). Off or loading
 * means nothing new renders anywhere. READ_ONLY (Pro Buyer switched off by
 * CS) may look, never change: `canEdit` is false and every share link is dead
 * server-side.
 *
 * The provider in `_app` calls this on every page, so the gateway is only
 * asked once both gates are on: everyone outside the gate costs nothing.
 * Read it through `useProWorkspace()`, not directly.
 */
export const useWorkspaceAccess = () => {
  const { isLoggedIn } = useAuth();
  const workspaces = useFeatureGate(PRO_WORKSPACES_GATE);
  const projects = useFeatureGate(PRO_BUYER_PROJECTS_UI_GATE);
  const gatesOn = workspaces.isEnabled && projects.isEnabled;
  const { data, loading } = useProBuyerProjectsAccess({
    skip: !isLoggedIn || !gatesOn,
  });
  const access = gatesOn ? data?.me?.proBuyerProjectsAccess ?? null : null;

  return {
    enabled:
      access === ProBuyerProjectsAccess.Full ||
      access === ProBuyerProjectsAccess.ReadOnly,
    canEdit: access === ProBuyerProjectsAccess.Full,
    readOnly: access === ProBuyerProjectsAccess.ReadOnly,
    loading:
      isLoggedIn &&
      (workspaces.isLoading ||
        projects.isLoading ||
        (gatesOn && loading && !data)),
  };
};
